import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, notification, Switch, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../services/api';
import EditClubModal from '../components/EditClubModal';

const ClubManagement = ({ card }) => {
  const [clubs, setClubs] = useState([]);
  const [_api, setApi] = notification.useNotification();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentClub, setCurrentClub] = useState({
    "club": "", "name": "", "show": false, "img": "", "url":"",
    "data": {"title": "", "subtitle": "", "tags": "", "powered": ""}
  });
  const [loading, setLoading] = useState(true);
  const [handledelete, sethandledelete] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // 批量操作相关状态
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedClubs, setSelectedClubs] = useState([]);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchAction, setBatchAction] = useState('show'); // 'delete' or 'show'
  const [showStatus, setShowStatus] = useState(true); // 默认开启状态

  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const setNotification = (config) => {
    _api.open(config);
  }

  // 加载社团列表
  useEffect(() => {
    const loadClubs = async () => {
      try {
        setLoading(true);
        const res = await api.getClubs();
        setClubs(res.data);
        setPagination(prev => ({
          ...prev,
          total: res.data.length
        }));
      } catch (e) {
        setNotification({
          type: "error",
          message: "错误",
          description: "加载社团失败:" + e.message
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadClubs();
  }, [card, refreshKey]);

  // 添加社团
  const handleAddClub = () => {
    setCurrentClub({
      "club": "", "name": "", "show": false, "isadd": true, "img": "","url":"", 
      "data": {"title": "", "subtitle": "", "tags": "", "powered": ""}
    });
    setEditModalVisible(true);
  };

  // 编辑社团
  const handleEditClub = (club) => {
    setCurrentClub(club);
    setEditModalVisible(true);
  };

  // 删除社团
  const handleDeleteClub = (club) => {
    setCurrentClub(club);
    sethandledelete(true);
  };

  // 批量操作处理函数
  const handleBatchAction = async () => {
    try {
      setLoading(true);
      let success_count = 0;
      const total_count = selectedClubs.length;
      
      // 准备所有操作请求
      const actionPromises = selectedClubs.map(async (clubId) => {
        try {
          if (batchAction === 'delete') {
            await api.deleteClub(clubId);
          } else if (batchAction === 'show') {
            const club = clubs.find(c => c.club === clubId);
            if (club) {
              await api.setClub({
                ...club,
                show: showStatus
              });
            }
          }
          success_count++;
        } catch (error) {
          console.error(`操作失败 ${clubId}:`, error);
        }
      });
      
      // 执行所有操作
      await Promise.all(actionPromises);
      
      // 显示结果通知
      setNotification({
        type: "info",
        message: `批量${batchAction === 'delete' ? '删除' : '修改'}完成`,
        description: `共 ${total_count} 条，成功: ${success_count} 条，失败: ${total_count - success_count} 条`
      });
      
      // 重置选择状态并刷新数据
      setSelectedClubs([]);
      setIsSelecting(false);
      setBatchModalVisible(false);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      setNotification({
        type: "error",
        message: `批量${batchAction === 'delete' ? '删除' : '修改'}失败`,
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // 保存社团信息 (修复的核心部分)
  const handleSaveClub = async (values) => {
    try {
      setLoading(true);
      
      // 重构数据结构以符合API要求
      const requestData = {
        ...values,
        img: `${values.club}.jpg`,
        // 保留原始图片路径或使用新上传的图片
        show: values.show || false
      };
      
      // 添加图片base64数据
      if (values.img_b) {
        requestData.img_b = values.img_b;
      }

      if (currentClub && !currentClub.isadd) {
        // 更新社团
        await api.setClub({
          ...requestData,
          club: currentClub.club
        });
        setNotification({
          "type": "success",
          "message": "成功",
          "description": "更新社团成功"
        });
      }
      if (currentClub.isadd) {
        // 添加新社团
        await api.addClub(requestData);
        setNotification({
          "type": "success",
          "message": "成功",
          "description": "添加社团成功"
        });
      }
      
      setEditModalVisible(false);
      setRefreshKey(prev => prev + 1);
    } catch (e) {
      setNotification({
        "type": "error",
        "message": "错误",
        "description": "操作失败:" + e.message
      });
    } finally {
      setLoading(false);
    }
  };

  // 行选择配置
  const rowSelection = isSelecting ? {
    selectedRowKeys: selectedClubs,
    onChange: (selectedRowKeys) => {
      setSelectedClubs(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.club === "Home" || !card
    })
  } : null;

  const columns = [
    {
      title: '社团ID',
      dataIndex: 'club',
      key: 'club',
    },
    {
      title: '社团名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => (
        <span style={{ color: record.show ? 'green' : 'red' }}>
          {record.show ? '已展示' : '未展示'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            disabled={!card}
            onClick={() => handleEditClub(record)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            danger
            disabled={record.club === "Home" || !card}
            onClick={() => handleDeleteClub(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {setApi}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            disabled={!card}
            onClick={handleAddClub}
            style={{ marginRight: 8 }}
          >
            添加社团
          </Button>
          
          <Button 
            type="primary" 
            onClick={() => setIsSelecting(!isSelecting)}
          >
            {isSelecting ? "取消选择" : "批量操作"}
          </Button>
          
          {isSelecting && selectedClubs.length > 0 && (
            <>
              <Button
                type="primary"
                onClick={() => {
                  setBatchAction('show');
                  setBatchModalVisible(true);
                }}
                style={{ marginLeft: 8 }}
              >
                批量设置状态
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => {
                  setBatchAction('delete');
                  setBatchModalVisible(true);
                }}
                style={{ marginLeft: 8 }}
              >
                批量删除
              </Button>
            </>
          )}
        </div>
      </div>

      <Table
        dataSource={clubs}
        columns={columns}
        loading={loading}
        rowKey="club"
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            setPagination(prev => ({
              ...prev,
              current: page,
              pageSize
            }));
          },
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: total => `共 ${total} 条`
        }}
      />

      {/* 单个删除确认对话框 */}
      <Modal 
        title="删除社团"
        visible={handledelete}
        onOk={async () => {
          try {
            setLoading(true);
            await api.deleteClub(currentClub.club);
            setNotification({
              "type": "success",
              "message": "成功",
              "description": "删除成功"
            });
            setRefreshKey(prev => prev + 1);
            sethandledelete(false);
          } catch (e) {
            setNotification({
              type: "error",
              "message": "错误",
              description: "删除失败:" + e.message
            });
          } finally {
            setLoading(false);
          }
        }}
        onCancel={() => sethandledelete(false)}
        okText="确认"
        okType='danger'
        cancelText="取消"
      >
        <p>确定删除社团 {currentClub.club} 吗？</p>
      </Modal>

      {/* 批量操作确认对话框 */}
      <Modal
        title={`批量${batchAction === 'delete' ? '删除' : '设置状态'}`}
        visible={batchModalVisible}
        onOk={handleBatchAction}
        onCancel={() => setBatchModalVisible(false)}
        okText="确认"
        okType={batchAction === 'delete' ? 'danger' : 'primary'}
        cancelText="取消"
      >
        {batchAction === 'delete' ? (
          <p>确定要删除选中的 {selectedClubs.length} 个社团吗？</p>
        ) : (
          <div>
            <p>确定要修改选中的 {selectedClubs.length} 个社团的展示状态吗？</p>
            <div style={{ marginTop: 16 }}>
              <span style={{ marginRight: 8 }}>设置状态为:</span>
              <Switch 
                checked={showStatus} 
                onChange={setShowStatus}
                checkedChildren="展示" 
                unCheckedChildren="隐藏" 
              />
            </div>
          </div>
        )}
      </Modal>

      <EditClubModal
        visible={editModalVisible}
        initialValues={currentClub}
        onOk={handleSaveClub}
        onCancel={() => setEditModalVisible(false)}
        isClub={true}
      />
    </div>
  );
};

export default ClubManagement;